import React, { useState, useEffect, useCallback } from "react";
import api from "../../../axiosConfig";
import Modal from "../../../components/Modal";
import "./LeaveManagement.css"; // Same CSS as your tab

export default function LeaveTypes() {
  const [types, setTypes] = useState([]);
  const [searchString, setSearchString] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [selectedType, setSelectedType] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  const token = localStorage.getItem("accessToken");

  const fetchTypes = useCallback(async (page) => {
    try {
      const res = await api.post(
        "/leave/type/list",
        {
          searchString,
          skip: (page - 1) * pageSize,
          take: pageSize,
          is_deleted: showDeleted,
        },
        { headers: { Authorization: token } },
      );
      const data = res.data.data;
      if (data) {
        setTypes(data);
        setTotalPages(res.data.pagination.totalPages);
        setCurrentPage(res.data.pagination.currentPage);
      }
    } catch (err) {
      console.error("Error fetching leave types:", err);
    }
  }, [searchString, pageSize, token, showDeleted]);

  useEffect(() => {
    fetchTypes(currentPage);
  }, [currentPage, searchString, fetchTypes, showDeleted]);

  const handleShowDeletedChange = (e) => {
    setShowDeleted(e.target.checked);
    setCurrentPage(1);
  } 

  const handleCreate = async (data) => {
    try {
      await api.post("/leave-type", data, { headers: { Authorization: token } });
      setIsCreateModalOpen(false);
      fetchTypes(currentPage);
    } catch (err) {
      console.error("Error creating leave type:", err);
    }
  };

  const handleEdit = (type) => {
    setSelectedType(type);
    setIsEditModalOpen(true);
  };

  const handleSave = async (data) => {
    try {
      await api.patch(`/leave-type/${data.id}`, data, { headers: { Authorization: token } });
      setIsEditModalOpen(false);
      fetchTypes(currentPage);
    } catch (err) {
      console.error("Error updating leave type:", err);
    }
  };

  const handleDeleteClick = (type) => {
    setSelectedType(type);
    setIsDeleteModalOpen(true);
    setSearchString("");
  };

  const handleDelete = async (data) => {
    try {
      await api.patch(`/leave-type/${data.id}/soft-delete`, null, { headers: { Authorization: token } });
      setIsDeleteModalOpen(false);
      fetchTypes(currentPage);
    } catch (err) {
      console.error("Error deleting leave type:", err);
    }
  };

  return (
    <div>
      <h2>Leave Types</h2>
      <div className="header-title">
        <div className="show-deleted-toggle">
            <input
                className="checkbox-style"
                type="checkbox"
                checked={showDeleted}
                onChange={handleShowDeletedChange}
            />
            <label className="center-align">Show Deleted</label>
        </div>
        <button className="add-button" onClick={() => setIsCreateModalOpen(true)}>
          Create Type
        </button>
      </div>


      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th className="name-col">Name</th>
              <th className="desc-col">Description</th>
              <th className="actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {types.length > 0 ? (
              types.map(t => (
                <tr key={t.id}>
                  <td className="name-col">{t.name}</td>
                  <td className="desc-col">{t.description || "-"}</td>
                  <td className="actions-col">
                    <button className="table-action-button edit" onClick={() => handleEdit(t)}>
                      Edit
                    </button>
                    <button className="table-action-button reject" onClick={() => handleDeleteClick(t)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" style={{ textAlign: "center" }}>No leave types available.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {types.length > 0 && (
        <div className="pagination">
          <button
            className="pagination-button"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button
            className="pagination-button"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create Leave Type"
        fields={[
          { name: "name", label: "Name", required: true },
          { name: "description", label: "Description", value: "" },
        ]}
        onSubmit={handleCreate}
        mode="create"
      />

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Leave Type"
        fields={[
          { name: "id", label: "ID", value: selectedType?.id, hidden: true },
          { name: "name", label: "Name", value: selectedType?.name },
          { name: "description", label: "Description", value: selectedType?.description },
        ]}
        onSubmit={handleSave}
        mode="edit"
      />

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Leave Type"
        message="Are you sure you want to delete this leave type?"
        fields={[
          { name: "id", label: "ID", value: selectedType?.id, hidden: true },
        ]}
        onSubmit={handleDelete}
        mode="delete"
      />
    </div>
  );
}
